import { useCallback } from "react";

import type { ModelViewerElement } from "@google/model-viewer/lib/model-viewer";
import { Button, ButtonIconWithLabel, Icon_Download24, isLoggedIn } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import useAccount from "../../../hooks/useAccount";
import { useProjectViewLinkSharing } from "../../../hooks/useProjectViewLink";
import { useTracker } from "../../../hooks/useTracker";
import { ElementClickAction } from "../../../lib/TrackingAdapter";
import { LoginButton } from "../components/LoginButton/LoginButton";
import { MenuHeader } from "../components/MenuHeader/MenuHeader";
import QRCodeFlyout from "../components/QRCodeFlyout/QRCodeFlyout";
import { ShareViewLinkModal } from "../components/ShareModal/ShareViewLinkModal";

import style from "./TopAppBarFloating.module.css";

interface TopAppBarFloatingProps {
  disableARButton?: boolean;
  hideARButton?: boolean;
  isDrawingsLayout?: boolean;
  modelViewer?: React.RefObject<ModelViewerElement>;
  downloadPdf?: (drawingId: string) => void;
  centerMessage?: string | React.ReactNode;
}

const TopAppBarFloating: React.FC<TopAppBarFloatingProps> = ({
  disableARButton,
  hideARButton,
  isDrawingsLayout = false,
  modelViewer,
  downloadPdf = () => null,
  centerMessage,
}) => {
  const tracker = useTracker();

  const { drawingId } = useParams();
  const { data: accountData, isPending: isAccountPending } = useAccount();
  const { data: projectViewLinkSharingData } = useProjectViewLinkSharing();

  const onDownloadPdfButtonclick = useCallback(() => {
    tracker.trackButtonClick({
      action: ElementClickAction.DOWNLOAD_PDF,
      elementId: "download-pdf-button",
    });

    if (drawingId && isDrawingsLayout) {
      downloadPdf(drawingId);
    }
  }, [drawingId, downloadPdf, isDrawingsLayout, tracker]);

  return (
    <div className={style.menuFloatingWrapper}>
      <div>{/* Grid placeholder for aligning center and right sections */}</div>
      <div>{centerMessage && <div className={style.menuFloatingCenter}>{centerMessage}</div>}</div>
      <div>
        <div className={style.menuRightFloating}>
          {!hideARButton && (
            <>
              {drawingId && isDrawingsLayout && (
                <Button
                  variant="borderless"
                  onClick={onDownloadPdfButtonclick}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <ButtonIconWithLabel icon={<Icon_Download24 />} label="PDF" />
                </Button>
              )}

              {!(drawingId && isDrawingsLayout) && (
                <QRCodeFlyout
                  className={style.arButton}
                  modelViewer={modelViewer}
                  disabled={disableARButton}
                  variant="borderless"
                />
              )}
            </>
          )}
          {projectViewLinkSharingData && <ShareViewLinkModal />}
          {isLoggedIn() && !isAccountPending && (
            <div className={style.menuHeaderWrapper}>
              <MenuHeader accountData={accountData} isCompact />
            </div>
          )}
          {!isLoggedIn() && (
            <>
              <div className={style.separator} />
              <LoginButton variant="soft" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopAppBarFloating;
